import React, { useState, useEffect } from 'react';
import { X, Database, HardDrive, Calendar, Info } from 'lucide-react';
import api from '../../../../services/api';

const TableDetailsModal = ({ table, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTableDetails();
  }, [table]);

  const fetchTableDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/database/tables/${table.database}/${table.tableName}/details`);
      setDetails(response.data);
    } catch (error) {
      console.error('Error fetching table details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!details) return null;

  const { metadata, columns, indexes } = details;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{metadata.tableName}</h2>
              <p className="text-sm text-gray-500">{metadata.database}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Table Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <HardDrive className="w-4 h-4 text-gray-500" />
                    <p className="text-sm text-gray-600">Engine</p>
                  </div>
                  <p className="font-semibold text-gray-900">{metadata.engine || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Rows</p>
                  <p className="font-semibold text-gray-900">{metadata.rowCount?.toLocaleString() || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Data Size</p>
                  <p className="font-semibold text-gray-900">{metadata.dataSize?.toFixed(2) || 0} MB</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Index Size</p>
                  <p className="font-semibold text-gray-900">{metadata.indexSize?.toFixed(2) || 0} MB</p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-blue-600 mb-1">Collation</p>
                    <p className="font-medium text-blue-900">{metadata.collation || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 mb-1">Created</p>
                    <p className="font-medium text-blue-900">
                      {metadata.created ? new Date(metadata.created).toLocaleString() : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 mb-1">Last Updated</p>
                    <p className="font-medium text-blue-900">
                      {metadata.updated ? new Date(metadata.updated).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>
                {metadata.comment && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <p className="text-sm text-blue-600 mb-1">Comment</p>
                    <p className="text-blue-900">{metadata.comment}</p>
                  </div>
                )}
              </div>

              {/* Columns */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Columns ({columns.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Type</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Nullable</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Default</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Max Length</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {columns.map((column, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{column.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{column.type}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                              column.nullable 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {column.nullable ? 'YES' : 'NO'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">{column.default || '-'}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{column.maxLength || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Indexes */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Indexes ({indexes.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Index Name</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Column</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {indexes.map((index, indexIdx) => (
                        <tr key={indexIdx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{index.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">{index.column}</td>
                          <td className="px-4 py-2 text-sm text-gray-600">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                              index.unique 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {index.unique ? 'UNIQUE' : 'INDEX'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableDetailsModal;
